// vg-binding: P7.D-44
// vg-binding: P7.D-46
// vg-binding: P7.D-51
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/pages/Payouts.tsx
//
// Phase 7 Wave 11 Task 39 — Payouts page.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageHeader, Pagination } from '@printway/ui';
import { formatCents } from '@printway/shared/currency';

import { BankAccountsManager } from '../features/payouts/BankAccountsManager.js';
import { PayoutScheduleCalendar } from '../features/payouts/PayoutScheduleCalendar.js';
import { WithdrawModal } from '../features/payouts/WithdrawModal.js';
import { WithdrawalsHistoryTable } from '../features/payouts/WithdrawalsHistoryTable.js';
import {
  useWalletSummary,
  useWithdrawalsHistory,
} from '../features/wallet/api.js';
import { useTranslation } from '../lib/i18n/index.js';

const HISTORY_PAGE_LIMIT = 20;

export function PayoutsPage(): JSX.Element {
  const { t } = useTranslation('wallet');
  const [modalOpen, setModalOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const summaryQ = useWalletSummary();
  const historyQ = useWithdrawalsHistory({ page: historyPage, limit: HISTORY_PAGE_LIMIT });
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const available = summaryQ.data?.available_cents ?? 0;

  // #2189 (2026-07-07): notification / "Mã rút tiền" deep-link →
  // scroll & flash-highlight the target row in the withdrawals history.
  //
  // The row rendered by WithdrawalsHistoryTable carries the FULL id in
  // `data-testid="withdrawal-row-<withdrawal_id>"`, but the visible
  // "Mã rút tiền" column shows only `withdrawal_id.slice(-8)`. A user
  // copying that short code (or a notification whose deep-link uses the
  // notification_id instead of the withdrawal_id) produced a `focus` value
  // that never matched the full-id selector → no scroll, no highlight.
  // Match by full id first, then fall back to a suffix (short-code) match so
  // both the full withdrawal_id and the displayed short code resolve.
  useEffect(() => {
    if (!focusId) return undefined;
    if (historyQ.isLoading) return undefined;
    const rows = Array.from(
      document.querySelectorAll<HTMLElement>('[data-testid^="withdrawal-row-"]'),
    );
    const rowId = (el: HTMLElement): string =>
      el.getAttribute('data-testid')?.replace('withdrawal-row-', '') ?? '';
    const target =
      rows.find((el) => rowId(el) === focusId) ??
      rows.find((el) => rowId(el).endsWith(focusId));
    if (!target) return undefined;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // ring (box-shadow) can render inconsistently on <tr>; pair it with a
    // background tint so the flash is reliably visible.
    const flashClasses = ['ring-2', 'ring-pw-primary', 'bg-pw-primary/10'];
    target.classList.add(...flashClasses);
    const timer = window.setTimeout(() => {
      target.classList.remove(...flashClasses);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [focusId, historyQ.isLoading, historyQ.data]);

  return (
    <main
      data-testid="payouts-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader
        title={t('payoutsPage.title')}
        actions={
          <button
            type="button"
            data-testid="payouts-open-withdraw-modal"
            onClick={() => setModalOpen(true)}
            disabled={available < 5000}
            className="inline-flex h-9 items-center rounded-md bg-pw-primary px-4 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('payoutsPage.openWithdrawCta')}
          </button>
        }
      />

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
          <h3 className="mb-1 text-sm font-semibold text-pw-text">{t('payoutsPage.availableBalance')}</h3>
          <p className="text-3xl font-semibold text-pw-text">
            {formatCents(available)}
          </p>
          <p className="mt-1 text-xs text-pw-text-muted">
            {t('payoutsPage.pendingPaidOutSummary', {
              pending: formatCents(summaryQ.data?.pending_cents ?? 0),
              paidOut: formatCents(summaryQ.data?.paid_out_cents ?? 0),
            })}
          </p>
          {summaryQ.data?.pending_withdraw_cents
            ? (
                <p className="mt-1 text-xs text-yellow-700">
                  {t('payoutsPage.pendingWithdrawNote', { amount: formatCents(summaryQ.data.pending_withdraw_cents) })}
                </p>
              )
            : null}
        </div>
        <PayoutScheduleCalendar />
      </section>

      <BankAccountsManager />

      <section className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('payoutsPage.historySection')}</h3>
        <WithdrawalsHistoryTable
          rows={historyQ.data?.rows ?? []}
          isLoading={historyQ.isLoading}
        />
        <Pagination
          currentPage={historyPage}
          pageCount={historyQ.data?.page_count ?? 0}
          totalRows={historyQ.data?.total ?? 0}
          testId="payouts-history-pagination"
          disabled={historyQ.isFetching}
          onChange={setHistoryPage}
        />
      </section>

      <WithdrawModal
        open={modalOpen}
        available_cents={available}
        onCancel={() => setModalOpen(false)}
        onSuccess={() => {
          setModalOpen(false);
          void historyQ.refetch();
          void summaryQ.refetch();
        }}
      />
    </main>
  );
}

export default PayoutsPage;
